import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format, parseISO } from 'date-fns';

const formatDate = (s) => {
  if (!s) return '—';
  try { return format(parseISO(s), 'MMM d, yyyy'); } catch (_) { return s; }
};
const formatMoney = (n) => `$${Number(n || 0).toFixed(2)}`;
const wrapText = (doc, text, maxWidth) => doc.splitTextToSize(String(text || ''), maxWidth);

export function generateWorkOrderPDF(wo) {
  if (!wo) throw new Error('No work order');
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 48;
  let y = margin;

  // ===== HEADER =====
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(15, 23, 42);
  doc.text('PROP VEIW', margin, y);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text('Property Management', margin, y + 14);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(15, 23, 42);
  doc.text('WORK ORDER', pageWidth - margin, y, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(71, 85, 105);
  doc.text(`#${wo.work_order_number || ''}`, pageWidth - margin, y + 16, { align: 'right' });

  y += 44;
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.5);
  doc.line(margin, y, pageWidth - margin, y);
  y += 20;

  // ===== FOR / DATES =====
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text('FOR', margin, y);
  doc.text('ISSUED', pageWidth - margin - 200, y);
  doc.text('SCHEDULED', pageWidth - margin, y, { align: 'right' });

  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.text(wo.client_name || wo.property_name || '—', margin, y + 15);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(71, 85, 105);
  if (wo.client_email) doc.text(wo.client_email, margin, y + 29);
  doc.text(`Property: ${wo.property_name || '—'}`, margin, y + 43);
  if (wo.worker_name) doc.text(`Assigned worker: ${wo.worker_name}`, margin, y + 57);

  doc.setFontSize(10);
  doc.setTextColor(15, 23, 42);
  doc.text(formatDate(wo.created_at), pageWidth - margin - 200, y + 15);
  const window = wo.scheduled_start && wo.scheduled_end
    ? `${formatDate(wo.scheduled_start)} — ${formatDate(wo.scheduled_end)}`
    : (wo.scheduled_start ? `Starts ${formatDate(wo.scheduled_start)}` : 'TBD');
  doc.text(window, pageWidth - margin, y + 15, { align: 'right' });

  if (wo.expected_duration_days) {
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(`Expected duration: ${wo.expected_duration_days} day${wo.expected_duration_days === 1 ? '' : 's'}`,
      pageWidth - margin, y + 30, { align: 'right' });
  }

  y += 78;

  // ===== SCOPE OF WORK =====
  if (wo.scope_of_work) {
    doc.setFillColor(248, 250, 252);
    const scopeLines = wrapText(doc, wo.scope_of_work, pageWidth - margin * 2 - 20);
    const scopeHeight = 26 + scopeLines.length * 12 + 8;
    doc.rect(margin, y, pageWidth - margin * 2, scopeHeight - 8, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text('SCOPE OF WORK', margin + 10, y + 14);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    doc.text(scopeLines, margin + 10, y + 28);
    y += scopeHeight + 4;
  }

  // ===== LINE ITEMS =====
  const laborRows = (wo.line_items || [])
    .filter((li) => li.item_type === 'labor')
    .map((li) => [
      li.description || '',
      `${li.quantity || 0} ${li.unit || ''}`.trim(),
      formatMoney(li.unit_price),
      formatMoney(li.amount),
    ]);
  const materialRows = (wo.line_items || [])
    .filter((li) => li.item_type === 'material')
    .map((li) => [
      li.description || '',
      `${li.quantity || 0} ${li.unit || ''}`.trim(),
      formatMoney(li.unit_price),
      formatMoney(li.amount),
    ]);

  const renderTable = (title, rows) => {
    if (!rows.length) return;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(title.toUpperCase(), margin, y + 12);
    autoTable(doc, {
      startY: y + 18,
      head: [['Description', 'Qty', 'Rate/Unit', 'Amount']],
      body: rows,
      theme: 'plain',
      styles: { fontSize: 9, cellPadding: 5, textColor: [15, 23, 42] },
      headStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9 },
      columnStyles: {
        1: { halign: 'right', cellWidth: 60 },
        2: { halign: 'right', cellWidth: 70 },
        3: { halign: 'right', cellWidth: 74 },
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: margin, right: margin },
    });
    y = doc.lastAutoTable.finalY + 8;
  };
  renderTable('Labor', laborRows);
  renderTable('Materials', materialRows);

  // ===== TOTALS =====
  const boxX = pageWidth - margin - 220;
  const boxW = 220;
  const row = (label, value, opts = {}) => {
    doc.setFont('helvetica', opts.bold ? 'bold' : 'normal');
    doc.setFontSize(opts.bold ? 11 : 10);
    doc.setTextColor(opts.bold ? 15 : 71, opts.bold ? 23 : 85, opts.bold ? 42 : 105);
    doc.text(label, boxX, y);
    doc.setTextColor(15, 23, 42);
    doc.text(value, boxX + boxW, y, { align: 'right' });
    y += 16;
  };

  row('Labor subtotal', formatMoney(wo.labor_subtotal));
  row('Materials subtotal', formatMoney(wo.materials_subtotal));
  if (wo.labor_adjustment) {
    row('Labor adjustment', (wo.labor_adjustment >= 0 ? '+' : '') + formatMoney(wo.labor_adjustment));
  }
  if (wo.tax_rate > 0) row(`Tax (${wo.tax_rate}%)`, formatMoney(wo.tax_amount));
  y += 4;
  doc.setDrawColor(15, 23, 42);
  doc.setLineWidth(1);
  doc.line(boxX, y - 8, boxX + boxW, y - 8);
  row('TOTAL', formatMoney(wo.total), { bold: true });

  // Cost range (if provided)
  if (wo.cost_range_min || wo.cost_range_max) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(
      `Estimated range: ${formatMoney(wo.cost_range_min)} — ${formatMoney(wo.cost_range_max)}`,
      boxX, y + 4
    );
    y += 20;
  }
  y += 12;

  // ===== BLOCKS =====
  const drawBlock = (title, text) => {
    if (!text) return;
    const contentW = pageWidth - margin * 2;
    const lines = wrapText(doc, text, contentW - 20);
    const blockH = 26 + lines.length * 12 + 8;
    if (y + blockH > pageHeight - margin - 60) { doc.addPage(); y = margin; }
    doc.setFillColor(248, 250, 252);
    doc.rect(margin, y, contentW, blockH - 8, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(title.toUpperCase(), margin + 10, y + 14);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    doc.text(lines, margin + 10, y + 28);
    y += blockH;
  };
  drawBlock('Message', wo.custom_message);
  drawBlock('Terms', wo.terms);
  drawBlock('Notes', wo.notes);

  // ===== SIGNATURE LINES =====
  if (y + 80 > pageHeight - margin) { doc.addPage(); y = margin; }
  y += 12;
  doc.setDrawColor(148, 163, 184);
  doc.setLineWidth(0.5);
  const sigW = (pageWidth - margin * 2 - 30) / 2;
  doc.line(margin, y + 30, margin + sigW, y + 30);
  doc.line(margin + sigW + 30, y + 30, margin + sigW * 2 + 30, y + 30);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text('Client signature', margin, y + 44);
  doc.text('Date', margin + sigW + 30, y + 44);
  y += 60;
  doc.line(margin, y + 30, margin + sigW, y + 30);
  doc.line(margin + sigW + 30, y + 30, margin + sigW * 2 + 30, y + 30);
  doc.text('Worker signature', margin, y + 44);
  doc.text('Date', margin + sigW + 30, y + 44);

  // ===== FOOTER =====
  const footerY = pageHeight - 32;
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.5);
  doc.line(margin, footerY, pageWidth - margin, footerY);
  doc.setFontSize(8);
  doc.setTextColor(148, 163, 184);
  doc.text('Generated by PROP VEIW • propveiw.com', margin, footerY + 14);
  doc.text(`Work Order ${wo.work_order_number || ''}`, pageWidth - margin, footerY + 14, { align: 'right' });

  const safeName = (wo.work_order_number || 'work-order').replace(/[^a-z0-9-_]/gi, '_');
  doc.save(`${safeName}.pdf`);
}
